import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  makeStyles,
  AccordionSummary,
  Card,
  CardMedia,
  CardContent
} from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import { colorSet } from '@components/Provider';
import { CustomizedTypography, CustomizedButton } from '@components/Customized';
import useRequest from '@hooks/useRequest';
import { CapabilityCardProps } from './CapabilityCard';
import useUserActionLog from '@hooks/useUserActionLog';
import { CapaActivityType, CapaQuotationServiceType } from '@generated/graphql';
import {
  DesignRequestStageEnum,
  PcbRequestStageEnum,
  RequestQueryString
} from '@routes/NewRequest/types';
import useQueryString from '@hooks/useQueryString';

interface CapabilityAccordionCardProps extends CapabilityCardProps {
  processmethod?: React.ReactNode;
  processmethodKey?: CapaQuotationServiceType;
  initializePcbServices: () => void;
  onHasRequestDialogOpen: () => void;
}

const CapabilityAccordionCard: React.FC<CapabilityAccordionCardProps> = ({
  img,
  label,
  processmethod,
  processmethodKey,
  log,
  initializePcbServices,
  onHasRequestDialogOpen
}) => {
  const [drawerState, setDrawerState] = useState(false);
  const isDesign = processmethodKey === CapaQuotationServiceType.Design;
  const isPcb = processmethodKey === CapaQuotationServiceType.Pcb;

  const {
    container,
    bottomDrawer,
    summaryCard,
    summaryCardImage,
    summaryCardContent,
    noProcessMethodBox,
    noProcessMethodRequest,
    noProcessMethodDescription
  } = useStyles(isDesign);
  const { hasNotEndedRequest } = useRequest();
  const history = useHistory();
  const { insertButtonLog, onInsertUserActionLogOne } = useUserActionLog();
  const { getQueryString } = useQueryString<RequestQueryString>();

  const onSendRequest = (requestInfo: RequestQueryString) => {
    history.push({
      pathname: '/request',
      search: getQueryString({
        serviceType: requestInfo.serviceType,
        process: requestInfo?.process?.toString() ?? undefined,
        detailedServiceType: requestInfo?.detailedServiceType,
        stage: isPcb
          ? PcbRequestStageEnum.PROCESS_SELECT
          : isDesign
          ? DesignRequestStageEnum.PROCESS_SELECT
          : undefined
      }),
      state: {
        fromLandingPage: true
      }
    });
  };

  const handleToggle = (open: boolean) => (
    event: React.KeyboardEvent | React.MouseEvent
  ) => {
    initializePcbServices();

    if (
      event &&
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerState(open);
  };

  return (
    <>
      <Box
        className={container}
        onClick={() => {
          if (log) {
            onInsertUserActionLogOne(log, CapaActivityType.ButtonClick);
          }
          setDrawerState(true);
        }}
      >
        <Card className={summaryCard}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <CardMedia className={summaryCardImage} image={img} />
            <CardContent className={summaryCardContent}>
              <CustomizedTypography
                variant="h4"
                fontWeight="500"
                color="textPrimary"
              >
                <i className="request-gtm-serviceType-btn">{label}</i>
              </CustomizedTypography>
            </CardContent>
          </AccordionSummary>
        </Card>
      </Box>
      <SwipeableDrawer
        classes={{ root: bottomDrawer }}
        anchor={'bottom'}
        open={drawerState}
        onClose={handleToggle(false)}
        onOpen={handleToggle(true)}
      >
        <div className="grayLine" />
        <CustomizedTypography
          variant="h4"
          fontWeight="700"
          color="textPrimary"
          gutterBottoms={isDesign ? 16 : 2}
        >
          {label}
          {!isDesign && !isPcb ? ' 세부 가공방식' : isDesign ? '' : ' 작업공정'}
        </CustomizedTypography>
        {!isDesign ? (
          <>
            <CustomizedTypography
              variant="body2"
              color="textSecondary"
              gutterBottoms={20}
            >
              {!isPcb
                ? '세부가공방식을 선택하여 견적을 요청해보세요.'
                : '필요한 작업을 선택하여 견적을 요청해보세요.'}
            </CustomizedTypography>
            <Box className={isPcb ? 'pcbContainer' : 'processContainer'}>
              {processmethod}
            </Box>
          </>
        ) : (
          <Box className={noProcessMethodBox}>
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              gutterBottoms={20}
              className={noProcessMethodDescription}
            >
              제품 외형에 대해 아이디어 스케치를 바탕을 3D 모델링 제작하는
              <b>제품디자인</b>부터, 제품 디자인이 실현될 수 있게 제조성을
              고려한 설계를 하는 <b>기구설계</b>와 실제양산을 위한{' '}
              <b>시제품 제작</b>까지 요청할 수 있는 서비스로, 도면없이
              참고이미지와 설명으로도 견적요청이 가능해요.
            </CustomizedTypography>
            <CustomizedButton
              variant="contained"
              color="primary"
              className={noProcessMethodRequest}
              fullWidth
              onClick={(e) => {
                e.stopPropagation();
                insertButtonLog(
                  'Button_Landing_ServiceTypeCard_DESIGN_Request'
                );
                if (hasNotEndedRequest) {
                  onHasRequestDialogOpen();
                } else {
                  onSendRequest({
                    serviceType: processmethodKey
                  });
                }
              }}
            >
              <CustomizedTypography
                variant="body1"
                fontWeight="500"
                className="designBtn"
              >
                디자인/설계 견적 요청하기
              </CustomizedTypography>
            </CustomizedButton>
          </Box>
        )}
      </SwipeableDrawer>
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    boxShadow: 'unset',
    '& .MuiAccordionSummary-root, .MuiAccordionSummary-content': {
      width: '100%',
      height: 80,
      padding: 0,
      margin: 0
    },
    '& .MuiAccordionSummary-root': {
      display: 'flex',
      justifyContent: 'space-between'
    },
    '&.MuiAccordion-rounded': {
      borderRadius: 16
    },
    '& .MuiAccordionSummary-expandIcon': {
      marginRight: 12
    },
    '&.MuiAccordion-root:before': {
      height: 0
    }
  },
  summaryCard: {
    display: 'flex',
    boxShadow: 'unset',
    backgroundColor: colorSet.gray300,
    borderRadius: 16
  },
  summaryCardImage: {
    minWidth: 80
  },
  summaryCardContent: {
    display: 'flex',
    alignItems: 'center',
    padding: '24px 20px'
  },
  bottomDrawer: {
    '& .MuiDrawer-paperAnchorBottom': {
      maxHeight: 510,
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '16px 16px 0px 0px',
      padding: '0 16px'
    },
    '& .grayLine': {
      width: 48,
      height: 4,
      borderRadius: 2,
      backgroundColor: colorSet.gray300,
      margin: '8px auto'
    },
    '& .processContainer': {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      minHeight: 392,
      marginBottom: 20,
      overflow: 'scroll'
    },
    '& .pcbContainer': {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      marginBottom: 20,
      overflow: 'scroll',
      '& button': {
        marginTop: 32
      }
    }
  },
  noProcessMethodBox: {
    '& .designBtn': {
      color: '#FFF'
    }
  },
  noProcessMethodRequest: {
    height: '48px !important',
    marginBottom: 24
  },
  noProcessMethodDescription: {
    wordBreak: 'break-all',
    '&.customized-button > .text:hover': {
      backgroundColor: colorSet.gray200
    }
  }
}));

export default CapabilityAccordionCard;
